"use server"

import { sdk } from "@lib/config"
import medusaError from "@lib/util/medusa-error"
import { isValidFiscalId, onlyDigits, type PersonType } from "@lib/util/fiscal"
import {
  AFFILIATE_METADATA_KEY,
  resolveAffiliateAttribution,
} from "@lib/util/affiliate"
import {
  isTermsAccepted,
  readTermsAcceptedAt,
  TERMS_ACCEPTED_AT_METADATA_KEY,
  TERMS_SAVE_FAILED_MESSAGE,
  TERMS_VERSION_METADATA_KEY,
} from "@modules/checkout/lib/terms-acceptance"
import { TERMS_LAST_MODIFIED_ISO } from "@modules/legal/lib/terms-content"
import { HttpTypes } from "@medusajs/types"
import { revalidateTag } from "next/cache"
import { redirect } from "next/navigation"
import {
  getAffiliateCode,
  getAuthHeaders,
  getCacheOptions,
  getCacheTag,
  getCartId,
  removeCartId,
  setCartId,
} from "./cookies"
import { listCartShippingMethods } from "./fulfillment"
import { getRegion } from "./regions"
import { getLocale } from "./locale-actions"

// Story 3.3: nome da opção de entrega R$0 dos Planos (não-físicos), auto-aplicada sem
// passo visível. Deve casar com o seed/reconcile do backend (`DIGITAL_DELIVERY_OPTION_NAME`).
const DIGITAL_DELIVERY_OPTION_NAME = "Entrega digital"
// Chave fiscal lida pelo comprovante (Story 2.4): CPF/CNPJ em dígitos CRUS.
const FISCAL_ID_METADATA_KEY = "cpf_cnpj"
const NEIGHBORHOOD_METADATA_KEY = "bairro"

// Story 10.12 — string vazia é o sentinela que o `mergeMetadata` do `@medusajs/utils` 2.17.2 lê
// como "apagar esta chave" (verificado na fonte do pacote, dentro do container). Nomeado porque um
// `""` solto no meio de um objeto de metadata parece descuido e é o oposto: é semântica.
//
// A Story 7.6 limpa o `affiliate_code` com `null` EXPLÍCITO — outra convenção, no mesmo ficheiro.
// As duas estão registadas no ledger de trabalho diferido; unificá-las não é escopo desta story.
const METADATA_DELETE_SENTINEL = ""

// País único da loja (single-region BR). O checkout não tem seletor de país.
const CHECKOUT_COUNTRY_CODE = "br"

// Validação de formato de e-mail no servidor (defesa-em-profundidade: o componente já
// valida no cliente, mas a server action é uma fronteira de confiança própria).
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Dados fiscais + endereço BR coletados no passo Cobrança (P2). */
export type BillingDetailsInput = {
  personType: PersonType
  /** PF: nome próprio. */
  firstName: string
  /** PF: apelido/sobrenome. Vazio para PJ. */
  lastName: string
  /** PJ: razão social. Vazio para PF. */
  companyName: string
  /** CPF (PF) ou CNPJ (PJ) — cru ou mascarado; persistido em dígitos crus. */
  fiscalId: string
  phone: string
  cep: string
  province: string
  city: string
  neighborhood: string
  street: string
  number: string
  complement: string
}

/** Resultado serializável de uma ação de passo do checkout. */
export type CheckoutStepResult = { ok: boolean; error?: string }

/**
 * Retrieves a cart by its ID. If no ID is provided, it will use the cart ID from the cookies.
 * @param cartId - optional - The ID of the cart to retrieve.
 * @returns The cart object if found, or null if not found.
 */
export async function retrieveCart(cartId?: string, fields?: string) {
  const id = cartId || (await getCartId())
  fields ??=
    "*items, *region, *items.product, *items.variant, *items.thumbnail, *items.metadata, +items.total, *promotions, +shipping_methods.name"

  if (!id) {
    return null
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("carts")),
  }

  return await sdk.client
    .fetch<HttpTypes.StoreCartResponse>(`/store/carts/${id}`, {
      method: "GET",
      query: {
        fields,
      },
      headers,
      next,
      cache: "force-cache",
    })
    .then(({ cart }: { cart: HttpTypes.StoreCart }) => cart)
    .catch(() => null)
}

export async function getOrSetCart(countryCode: string) {
  const region = await getRegion(countryCode)

  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`)
  }

  let cart = await retrieveCart(undefined, "id,region_id")

  const headers = {
    ...(await getAuthHeaders()),
  }

  if (!cart) {
    const locale = await getLocale()
    const cartResp = await sdk.store.cart.create(
      { region_id: region.id, locale: locale || undefined },
      {},
      headers
    )
    cart = cartResp.cart

    await setCartId(cart.id)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  if (cart && cart?.region_id !== region.id) {
    await sdk.store.cart.update(cart.id, { region_id: region.id }, {}, headers)
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  return cart
}

export async function updateCart(data: HttpTypes.StoreUpdateCart) {
  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("No existing cart found, please create one before updating")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.cart
    .update(cartId, data, {}, headers)
    .then(async ({ cart }: { cart: HttpTypes.StoreCart }) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)

      return cart
    })
    .catch(medusaError)
}

export async function addToCart({
  variantId,
  quantity,
  countryCode,
}: {
  variantId: string
  quantity: number
  countryCode: string
}) {
  if (!variantId) {
    throw new Error("Missing variant ID when adding to cart")
  }

  const cart = await getOrSetCart(countryCode)

  if (!cart) {
    throw new Error("Error retrieving or creating cart")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .createLineItem(
      cart.id,
      {
        variant_id: variantId,
        quantity,
      },
      {},
      headers
    )
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)
    })
    .catch(medusaError)
}

export async function updateLineItem({
  lineId,
  quantity,
}: {
  lineId: string
  quantity: number
}) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when updating line item")
  }

  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("Missing cart ID when updating line item")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .updateLineItem(cartId, lineId, { quantity }, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)
    })
    .catch(medusaError)
}

export async function deleteLineItem(lineId: string) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when deleting line item")
  }

  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("Missing cart ID when deleting line item")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .deleteLineItem(cartId, lineId, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)
    })
    .catch(medusaError)
}

/**
 * Story 3.3 (AD-16, metade storefront) — SUBSTITUI o conteúdo do carrinho por um único
 * Plano (limpa as linhas existentes e adiciona a nova). "1 Plano = 1 carrinho": trocar
 * de Plano na vitrine é substituição, não acumulação. Lança em falha (o subscribe-button
 * mostra o erro inline e mantém o carrinho intacto).
 */
export async function replaceCartWithVariant({
  variantId,
  quantity,
  countryCode,
}: {
  variantId: string
  quantity: number
  countryCode: string
}): Promise<void> {
  if (!variantId) {
    throw new Error("Missing variant ID when replacing cart")
  }

  const cart = await getOrSetCart(countryCode)
  if (!cart) {
    throw new Error("Error retrieving or creating cart")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  // Esvazia as linhas atuais antes de adicionar a nova (substituição, não anexação).
  // Falha loud se o carrinho não for legível: um `?? []` silencioso pularia os deletes e
  // ANEXARIA a nova linha ao carrinho ainda cheio (>1 item ⇒ a guarda bloquearia a compra).
  //
  // LEITURA FRESCA, não `retrieveCart` (QA 2026-07-19, VITRINE-E004 ≡ CHECKOUT-E013): o
  // `retrieveCart` lê com `cache: "force-cache"`. Uma leitura servida do cache mostra a lista
  // de linhas ANTERIOR — o laço abaixo apaga o que já não existe, não apaga o que existe, e a
  // nova linha é ANEXADA. O comprador chegava ao PayPal com 2 Planos e só batia na guarda
  // 1-Plano-1 (AD-16) DEPOIS de autorizar: beco-sem-saída no fim do funil. A substituição
  // depende de ver o estado ATUAL, logo não pode passar por cache.
  const { cart: existing } = await sdk.client.fetch<HttpTypes.StoreCartResponse>(
    `/store/carts/${cart.id}`,
    {
      method: "GET",
      query: { fields: "id,*items" },
      headers,
      cache: "no-store",
    }
  )
  if (!existing) {
    throw new Error("Error reading cart to replace its contents")
  }
  for (const item of existing.items ?? []) {
    await sdk.store.cart.deleteLineItem(cart.id, item.id, {}, headers)
  }

  await sdk.store.cart.createLineItem(
    cart.id,
    { variant_id: variantId, quantity },
    {},
    headers
  )

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)

  const fulfillmentCacheTag = await getCacheTag("fulfillment")
  revalidateTag(fulfillmentCacheTag)
}

/**
 * P1 Identificação — grava o e-mail de contato no carrinho. Não cria conta (a conta é
 * criada na captura do pagamento, Story 3.4). Devolve resultado serializável; o cliente
 * navega para o passo Cobrança em caso de sucesso.
 */
export async function setContactEmail(
  email: string
): Promise<CheckoutStepResult> {
  const trimmed = email.trim()
  if (!EMAIL_PATTERN.test(trimmed)) {
    return { ok: false, error: "Informe um e-mail válido." }
  }
  try {
    await updateCart({ email: trimmed })
    return { ok: true }
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Não foi possível salvar o e-mail.",
    }
  }
}

/**
 * P2 Cobrança — persiste o fiscal + endereço BR e auto-aplica a entrega R$0.
 *
 * Coerência com a Story 2.4 (leitor do comprovante): CPF/CNPJ em DÍGITOS CRUS na chave
 * `cpf_cnpj` tanto no `cart.metadata` (→ `order.metadata`, leitura primária) como no
 * `billing_address.metadata` (leitura de fallback). Nome em `first_name`/`last_name`
 * (PJ: razão social em `first_name`+`company`). O mesmo endereço serve billing e shipping
 * (a conclusão exige shipping address para itens shippable).
 */
export async function saveBillingDetails(
  details: BillingDetailsInput
): Promise<CheckoutStepResult> {
  const cartId = await getCartId()
  if (!cartId) {
    return { ok: false, error: "Carrinho não encontrado." }
  }

  // Validação server-side (defesa-em-profundidade): nunca persistir um documento com DV
  // inválido — o comprovante (Story 2.4) trata !=11/14 dígitos como ausente.
  if (!isValidFiscalId(details.fiscalId, details.personType)) {
    return { ok: false, error: "Documento fiscal (CPF/CNPJ) inválido." }
  }

  const fiscalDigits = onlyDigits(details.fiscalId)
  const isCompany = details.personType === "pj"
  const company = details.companyName.trim()

  const address = {
    first_name: isCompany ? company : details.firstName.trim(),
    last_name: isCompany ? "" : details.lastName.trim(),
    company: isCompany ? company : "",
    address_1: details.street.trim(),
    address_2: buildAddressLine2(details.number, details.complement),
    postal_code: onlyDigits(details.cep),
    city: details.city.trim(),
    province: details.province.trim().toUpperCase(),
    country_code: CHECKOUT_COUNTRY_CODE,
    phone: onlyDigits(details.phone),
    metadata: {
      [FISCAL_ID_METADATA_KEY]: fiscalDigits,
      [NEIGHBORHOOD_METADATA_KEY]: details.neighborhood.trim(),
    },
  }

  try {
    // Preserva metadata existente do carrinho (não faz clobber): só (re)escreve a chave
    // fiscal. Leitura primária do comprovante (Story 2.4): order.metadata.cpf_cnpj cru.
    const current = await retrieveCart(cartId, "id,+metadata")
    await updateCart({
      shipping_address: address,
      billing_address: address,
      metadata: {
        ...(current?.metadata ?? {}),
        [FISCAL_ID_METADATA_KEY]: fiscalDigits,
      },
    } as HttpTypes.StoreUpdateCart)

    // Planos são não-físicos: aplica a opção R$0 "Entrega digital" (sem passo visível),
    // mantendo o total = preço do Plano. A conclusão do carrinho exige um método de envio.
    await applyDigitalDeliveryMethod(cartId)

    return { ok: true }
  } catch (e) {
    return {
      ok: false,
      error:
        e instanceof Error
          ? e.message
          : "Não foi possível salvar os dados de cobrança.",
    }
  }
}

/**
 * Story 10.12 — grava (ou retira) o aceite dos Termos na `cart.metadata`, que o complete-cart copia
 * para a `order.metadata`. É o registo de consentimento da compra.
 *
 * Envia SÓ as duas chaves desta função. O `mergeMetadata` do `@medusajs/utils` 2.17.2 funde por
 * CHAVE (lido na fonte, dentro do container), logo espalhar o retrato atual da metadata não protege
 * nada: só reenvia uma cópia velha do `cpf_cnpj`/`affiliate_code` e pode reverter uma escrita
 * concorrente. Retirar o aceite escreve `""` — o sentinela que o mesmo `mergeMetadata` usa para
 * APAGAR a chave (um `delete` não existe na Store API e omitir preserva o valor antigo).
 *
 * Ao (re)marcar, PRESERVA o instante já gravado se o carrinho já traz aceite válido da versão
 * publicada: o campo é o registo de QUANDO se consentiu, não do último clique.
 *
 * O carrinho DEVOLVIDO é verificado nas duas direções. A da recusa não basta ser "ilegível": se o
 * sentinela deixasse de apagar, o `""` guardado à letra passaria por retirada confirmada e entraria
 * na `order.metadata` a fazer de registo.
 */
export async function setTermsAcceptance(
  accepted: boolean
): Promise<CheckoutStepResult> {
  try {
    // A leitura do cookie fica DENTRO do `try`: se ela rejeitar (o `cookies()` do Next lança fora
    // de um request scope), a exceção subia até ao `onChange` da checkbox — uma promessa rejeitada
    // no meio do gesto obrigatório — em vez do desfecho PT-BR que o chamador espera.
    const cartId = await getCartId()
    if (!cartId) {
      return { ok: false, error: TERMS_SAVE_FAILED_MESSAGE }
    }

    const current = await retrieveCart(cartId, "id,+metadata")
    // Só o aceite VÁLIDO da versão publicada preserva o instante: um registo de outra versão (ou
    // ilegível) não é o consentimento que está a ser dado agora.
    const acceptedAt = isTermsAccepted(current)
      ? readTermsAcceptedAt(current) ?? new Date().toISOString()
      : new Date().toISOString()

    const updated = await updateCart({
      metadata: {
        [TERMS_ACCEPTED_AT_METADATA_KEY]: accepted
          ? acceptedAt
          : METADATA_DELETE_SENTINEL,
        [TERMS_VERSION_METADATA_KEY]: accepted
          ? TERMS_LAST_MODIFIED_ISO
          : METADATA_DELETE_SENTINEL,
      },
    } as HttpTypes.StoreUpdateCart)

    if (accepted) {
      if (!isTermsAccepted(updated)) {
        return { ok: false, error: TERMS_SAVE_FAILED_MESSAGE }
      }
      return { ok: true }
    }

    const metadata = updated?.metadata ?? {}
    const stillThere =
      TERMS_ACCEPTED_AT_METADATA_KEY in metadata ||
      TERMS_VERSION_METADATA_KEY in metadata
    if (stillThere) {
      return { ok: false, error: TERMS_SAVE_FAILED_MESSAGE }
    }
    return { ok: true }
  } catch (e) {
    // NUNCA encaminhar `e.message`: o `updateCart` termina em `medusaError`, que lança sempre
    // `Error` com o texto cru do backend, em inglês. Fica no log, não no ecrã do consentimento.
    console.error("setTermsAcceptance: falha ao gravar o aceite dos Termos:", e)
    return { ok: false, error: TERMS_SAVE_FAILED_MESSAGE }
  }
}

/** Combina número + complemento numa única linha de endereço (`address_2`). */
function buildAddressLine2(streetNumber: string, complement: string): string {
  return [streetNumber.trim(), complement.trim()].filter(Boolean).join(" - ")
}

/**
 * Auto-aplica a opção de entrega R$0 "Entrega digital" ao carrinho. Prefere o casamento
 * por nome; cai para a primeira opção de custo zero. Falha loud se nenhuma existir (a
 * conclusão exige método de envio) — o operador deve rodar o script de reconciliação.
 */
async function applyDigitalDeliveryMethod(cartId: string): Promise<void> {
  const options = await listCartShippingMethods(cartId)
  if (!options || options.length === 0) {
    throw new Error("Nenhuma opção de entrega disponível para o carrinho.")
  }
  // Fallback estrito: `amount === 0` (NÃO `amount ?? 0`) — uma opção com `amount`
  // indefinido NUNCA pode ser tratada como grátis (evita anexar uma taxa de frete real
  // ao total do Plano). Se nem o nome nem uma R$0 explícita casarem, falha loud abaixo.
  const digital =
    options.find((option) => option.name === DIGITAL_DELIVERY_OPTION_NAME) ??
    options.find((option) => option.amount === 0)
  if (!digital) {
    throw new Error(
      `Opção de entrega "${DIGITAL_DELIVERY_OPTION_NAME}" não encontrada. Rode o script de reconciliação da região.`
    )
  }
  await setShippingMethod({ cartId, shippingMethodId: digital.id })
}

export async function setShippingMethod({
  cartId,
  shippingMethodId,
}: {
  cartId: string
  shippingMethodId: string
}) {
  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.cart
    .addShippingMethod(cartId, { option_id: shippingMethodId }, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)
    })
    .catch(medusaError)
}

export async function initiatePaymentSession(
  cart: HttpTypes.StoreCart,
  data: HttpTypes.StoreInitializePaymentSession
) {
  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.payment
    .initiatePaymentSession(cart, data, {}, headers)
    .then(async (resp) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)
      return resp
    })
    .catch(medusaError)
}

export async function applyPromotions(codes: string[]) {
  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("No existing cart found")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  // Story 7.6 (Fatia 2) — o cupom É o canal de atribuição do afiliado (ARQUEIRO15), logo o
  // carimbo do `affiliate_code` acontece AQUI, no APPLY (decisão do Diego), e não só no
  // `placeOrder`. O complete-cart copia `cart.metadata` → `order.metadata`, de onde o
  // subscriber money-first (`payment.captured`) resolve a atribuição e a comissão.
  //
  // O cupom tem precedência sobre o cookie `?ref` (ver `resolveAffiliateAttribution`);
  // remover o cupom faz a atribuição recair no cookie, se houver, ou some.
  //
  // FAIL-SOFT (money-first): a atribuição é acessória — falhar aqui NUNCA pode impedir o
  // comprador de aplicar ou remover um desconto. Merge não-clobber: escrever a metadata
  // inteira apagaria o que já lá está (ex.: o fiscal `cpf_cnpj`). Sem base para o merge
  // (leitura degradada a `null`), pula-se o carimbo — o `placeOrder` volta a resolvê-lo.
  let metadata: Record<string, unknown> | undefined
  try {
    const current = await retrieveCart(cartId, "id,+metadata")
    if (current) {
      const attributed = resolveAffiliateAttribution({
        couponCodes: codes,
        cookieCode: await getAffiliateCode(),
      })
      // `null` EXPLÍCITO para limpar, nunca `delete`: a Store API do Medusa faz MERGE da
      // metadata — omitir a chave preserva o valor antigo (verificado em container). Sem
      // isto, remover o cupom deixava a atribuição carimbada e o afiliado era creditado numa
      // venda a preço cheio. O subscriber da captura trata não-string como "sem atribuição".
      metadata = {
        ...(current.metadata ?? {}),
        [AFFILIATE_METADATA_KEY]: attributed,
      }
    }
  } catch (affiliateError) {
    console.error(
      "applyPromotions: falha ao carimbar o código de afiliado (fail-soft, desconto segue):",
      affiliateError,
    )
  }

  return sdk.store.cart
    .update(
      cartId,
      { promo_codes: codes, ...(metadata ? { metadata } : {}) },
      {},
      headers,
    )
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)
    })
    .catch(medusaError)
}

// RETIRADOS (inventário do starter, 2026-07-20) — cinco server actions mortas do starter, sem um
// único importador em todo o storefront:
//
//   applyGiftCard / removeGiftCard / removeDiscount — stubs de gift card com o CORPO INTEIRO
//     comentado desde o fork. Esta loja não vende nem aceita gift cards; o desconto real é a
//     Promotion nativa do Medusa (cupom de afiliado, Story 7.6), noutro caminho.
//   submitPromotionForm — órfã; o cupom é aplicado por `applyPromotions` a partir do
//     `discount-code` do checkout.
//   setAddresses — recolha de morada de ENTREGA (shipping + billing) para bens físicos, com
//     redirect para `/checkout?step=pagamento`. Planos são não-físicos e não há passo de
//     entrega; a recolha fiscal vive em `saveBillingDetails`.
//
// Eram também a origem dos 8 erros de lint pré-existentes deste ficheiro (`no-unused-vars` nos
// parâmetros dos stubs e `no-explicit-any` nos `catch (e: any)`/`as any`).

/**
 * Places an order for a cart. If no cart ID is provided, it will use the cart ID from the cookies.
 * @param cartId - optional - The ID of the cart to place an order for.
 * @returns The cart object if the order was successful, or null if not.
 */
export async function placeOrder(cartId?: string) {
  const id = cartId || (await getCartId())

  if (!id) {
    throw new Error("No existing cart found when placing an order")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  // Story 7.1 — carimba o código de afiliado (cookie de atribuição) na `metadata` do carrinho ANTES
  // de concluir; o Medusa (complete-cart, v2.17.2) copia `cart.metadata` → `order.metadata`, de onde
  // o subscriber money-first (`payment.captured`) resolve a atribuição.
  // FAIL-SOFT (invariante money-first): a atribuição é acessória — um erro aqui NUNCA pode impedir a
  // conclusão de uma compra paga; espelha o fail-soft do subscriber do Engine. Sanitiza o valor do
  // cookie (defesa contra cookie forjado/oversized no server) e carimba o carrinho `id` que SERÁ
  // concluído (não o do cookie), enviando SÓ a sua chave — é o `mergeMetadata` do Medusa que
  // preserva as restantes (funde por chave), não um espalhamento nosso.
  try {
    // `retrieveCart` ENGOLE erros de fetch a `null` (não lança), logo um blip transitório na
    // leitura NÃO cai no catch abaixo. A leitura é precisa para resolver a PRECEDÊNCIA do cupom
    // (abaixo), não para reenviar a metadata: o `mergeMetadata` do Medusa funde por chave, logo
    // escrever só `affiliate_code` NÃO apaga o `cpf_cnpj` nem nenhuma outra chave. Sem leitura ⇒
    // não há como resolver a precedência ⇒ pular o carimbo (fail-soft: perder a atribuição é
    // preferível a atribuir à fonte errada).
    //
    // Story 7.6 (Fatia 2): resolve com PRECEDÊNCIA — o cupom aplicado ganha do cookie `?ref`.
    // Sem isto, este carimbo (que corre depois do apply) sobrescreveria com um cookie antigo a
    // atribuição do cupom que o comprador acabou de digitar.
    const current = await retrieveCart(id, "id,+metadata,promotions.code")
    if (current) {
      const affiliateCode = resolveAffiliateAttribution({
        couponCodes: (current.promotions ?? []).map((promotion) => promotion.code),
        cookieCode: await getAffiliateCode(),
      })
      if (affiliateCode) {
        // SÓ a sua própria chave. Espalhar `current.metadata` aqui não protege nada (o Medusa
        // funde por chave) e RESSUSCITA o que tiver sido apagado entre esta leitura e esta
        // escrita — nomeadamente o `terms_accepted_at`/`terms_version` que o comprador acabou de
        // retirar, gravando na `order.metadata` um consentimento que já não existe.
        await sdk.store.cart.update(
          id,
          {
            metadata: {
              [AFFILIATE_METADATA_KEY]: affiliateCode,
            },
          },
          {},
          headers,
        )
      }
    }
  } catch (affiliateError) {
    console.error(
      "placeOrder: falha ao carimbar o código de afiliado (fail-soft, compra segue):",
      affiliateError,
    )
  }

  const cartRes = await sdk.store.cart
    .complete(id, {}, headers)
    .then(async (cartRes) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)
      return cartRes
    })
    .catch(medusaError)

  if (cartRes?.type === "order") {
    const orderCacheTag = await getCacheTag("orders")
    revalidateTag(orderCacheTag)

    removeCartId()
    // Story 3.7 — o token assinado viaja no redirect: é a ÚNICA janela em que se pode
    // entregá-lo ao convidado com segurança (ele não tem sessão, e qualquer forma de o
    // pedir depois teria de se autorizar pelo `order_id`, o portador que a story elimina).
    const orderToken = (cartRes?.order as { metadata?: Record<string, unknown> } | undefined)
      ?.metadata?.order_token
    const confirmedPath = `/order/${cartRes?.order.id}/confirmed`
    redirect(
      typeof orderToken === "string" && orderToken.length > 0
        ? `${confirmedPath}?t=${encodeURIComponent(orderToken)}`
        : confirmedPath,
    )
  }

  return cartRes.cart
}

// RETIRADO (2026-07-20): `listCartOptions` existia só para alimentar o `FreeShippingPriceNudge`
// do starter — um popup de "frete grátis" montado no layout do grupo `(main)`, ou seja, sobre
// TODAS as rotas de uma loja que vende VPS. Não há frete. Com o nudge saiu também este fetch a
// `/store/shipping-options`, que corria em cada render de cada página com carrinho.
